/**
 * Frame parsing for `text/event-stream` responses read over `fetch`.
 *
 * Native `EventSource` cannot set an `Authorization` header, and every
 * streaming endpoint here is authenticated, so the dashboard reads SSE off a
 * `fetch` body instead and parses the frames itself. Both consumers — the agent
 * chat stream and the email-status stream — share this so the buffer handling
 * exists once: a chunk boundary can fall anywhere, including mid-frame and
 * mid-UTF-8-sequence, which is what `{ stream: true }` on the decoder handles.
 */

/**
 * Yield the JSON payload of each complete `data:` frame in an SSE body.
 *
 * Malformed frames are skipped rather than thrown: one bad frame must not end
 * a stream that is still delivering good ones. Comment frames (`: ping`),
 * which keep idle proxies from reaping the connection, carry no `data:` line
 * and are skipped by the same rule.
 */
export async function* parseSseFrames<T>(body: ReadableStream<Uint8Array>): AsyncGenerator<T> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        const dataLine = frame.split('\n').find((line) => line.startsWith('data:'));
        if (!dataLine) continue;
        try {
          yield JSON.parse(dataLine.slice(5).trim()) as T;
        } catch {
          // Skip malformed frames rather than killing the stream.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

/** One complete SSE frame with its `id:` and `event:` lines, for streams that resume. */
export interface SseEvent<T> {
  /** The frame's `id:` line, or null. Send the last one back as `Last-Event-ID`. */
  id: string | null;
  event: string | null;
  data: T;
}

/**
 * Like `parseSseFrames`, but keeps each frame's `id:` and `event:` lines —
 * what a resumable stream (the Support agent stream) needs to reconnect
 * without a gap. Malformed and comment-only frames are skipped the same way.
 */
export async function* parseSseEvents<T>(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<SseEvent<T>> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf('\n\n');

        const lines = frame.split('\n');
        const dataLine = lines.find((line) => line.startsWith('data:'));
        if (!dataLine) continue;
        const field = (name: string): string | null => {
          const line = lines.find((l) => l.startsWith(`${name}:`));
          return line ? line.slice(name.length + 1).trim() : null;
        };
        try {
          yield {
            id: field('id'),
            event: field('event'),
            data: JSON.parse(dataLine.slice(5).trim()) as T,
          };
        } catch {
          // Skip malformed frames rather than killing the stream.
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}
